<script setup>
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const OPERATOR_OPTIONS = computed(() => [
  { id: 'equal_to',                 name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_EQUAL_TO') },
  { id: 'not_equal_to',             name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_NOT_EQUAL_TO') },
  { id: 'contains',                 name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_CONTAINS') },
  { id: 'not_contains',             name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_NOT_CONTAINS') },
  { id: 'greater_than',             name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_GREATER_THAN') },
  { id: 'less_than',                name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_LESS_THAN') },
  { id: 'greater_than_or_equal_to', name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_GREATER_THAN_OR_EQUAL_TO') },
  { id: 'less_than_or_equal_to',    name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_LESS_THAN_OR_EQUAL_TO') },
  { id: 'is_present',               name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_IS_PRESENT') },
  { id: 'is_empty',                 name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OP_IS_EMPTY') },
]);

const NO_VALUE_OPS = new Set(['is_present', 'is_empty']);

const QUERY_OPTIONS = computed(() => [
  { id: 'and', name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.QUERY_AND') },
  { id: 'or',  name: t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.QUERY_OR') },
]);

const conditions = computed(() => props.modelValue?.conditions ?? []);

function emitConditions(list) {
  const updated = list.map((c, i) => ({
    ...c,
    query_operator: i < list.length - 1 ? (c.query_operator || 'and') : null,
  }));
  update({ conditions: updated });
}

function addCondition() {
  emitConditions([...conditions.value, { field: '', operator: 'equal_to', value: '', query_operator: 'and' }]);
}

function removeCondition(i) {
  emitConditions(conditions.value.filter((_, idx) => idx !== i));
}

function patchCondition(i, obj) {
  emitConditions(conditions.value.map((c, idx) => (idx === i ? { ...c, ...obj } : c)));
}

// Variable picker – variable_name field
const showVarPicker = ref(false);
const varSearch = ref('');
const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q));
});

function selectVar(key) {
  update({ variable_name: `{{${key}}}` });
  showVarPicker.value = false;
  varSearch.value = '';
}

// Variable picker – condition value fields
const openPickerIndex = ref(null);
const variableSearch  = ref('');

const filteredCondVars = computed(() => {
  const q = variableSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q));
});

function togglePicker(i) {
  openPickerIndex.value = openPickerIndex.value === i ? null : i;
  variableSearch.value = '';
}

function insertVariable(i, key) {
  patchCondition(i, { value: `{{${key}}}` });
  openPickerIndex.value = null;
  variableSearch.value = '';
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Array variable -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.ARRAY_VARIABLE') }}</span>
      <div v-on-click-outside="() => { showVarPicker = false; varSearch = ''; }" class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            type="text"
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
            :value="modelValue?.variable_name ?? ''"
            :placeholder="t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.ARRAY_VARIABLE_PLACEHOLDER')"
            @input="update({ variable_name: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="showVarPicker = !showVarPicker"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="showVarPicker"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="varSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="selectVar(v.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Condições -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.CONDITIONS') }}</span>

      <div class="flex flex-col gap-3">
        <template v-for="(cond, i) in conditions" :key="i">

          <!-- AND/OR connector -->
          <div v-if="i > 0" class="flex items-center gap-2">
            <div class="h-px flex-1 bg-n-weak" />
            <div class="multiselect-wrap--small w-16">
              <MultiselectDropdown
                :options="QUERY_OPTIONS"
                :selected-item="QUERY_OPTIONS.find(o => o.id === (conditions[i - 1].query_operator || 'and'))"
                :hide-search="true"
                :has-thumbnail="false"
                @select="v => patchCondition(i - 1, { query_operator: v?.id ?? 'and' })"
              />
            </div>
            <div class="h-px flex-1 bg-n-weak" />
          </div>

          <!-- Condition card -->
          <div class="rounded-xl border border-n-weak bg-n-solid-1">

            <!-- Row 1: campo + delete -->
            <div class="flex items-center gap-1.5 px-2 py-1.5 bg-n-solid-2 border-b border-n-weak rounded-t-xl">
              <span class="text-[11px] font-medium text-n-slate-9 shrink-0">{{
                t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.IF')
              }}</span>
              <div class="flex-1 min-w-0 flex items-center gap-1 px-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                <input
                  class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                  :value="cond.field"
                  :placeholder="
                    t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.FIELD_PLACEHOLDER')
                  "
                  @input="patchCondition(i, { field: $event.target.value })"
                />
              </div>
              <button
                type="button"
                class="i-lucide-x size-3 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors"
                @click="removeCondition(i)"
              />
            </div>

            <!-- Row 2: operador + valor -->
            <div class="flex items-start gap-1.5 px-2 py-1.5">
              <div class="multiselect-wrap--small flex-1 min-w-0">
                <MultiselectDropdown
                  :options="OPERATOR_OPTIONS"
                  :selected-item="OPERATOR_OPTIONS.find(o => o.id === cond.operator) ?? null"
                  :multiselector-placeholder="
                    t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OPERATOR_PLACEHOLDER')
                  "
                  :has-thumbnail="false"
                  :hide-search="true"
                  @select="v => patchCondition(i, { operator: v?.id ?? 'equal_to' })"
                />
              </div>

              <template v-if="!NO_VALUE_OPS.has(cond.operator)">
                <div class="relative flex-1 min-w-0">
                  <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                    <input
                      type="text"
                      class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                      :value="cond.value"
                      :placeholder="
                        t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.VALUE_PLACEHOLDER')
                      "
                      @input="patchCondition(i, { value: $event.target.value })"
                    />
                    <button
                      type="button"
                      class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
                      :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
                      @click.stop="togglePicker(i)"
                    >
                      <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
                    </button>
                  </div>
                  <div
                    v-if="openPickerIndex === i"
                    class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
                  >
                    <div class="px-2 pt-2 pb-1">
                      <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                        <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                        <input
                          v-model="variableSearch"
                          type="text"
                          class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                          :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                          autofocus
                        />
                      </div>
                    </div>
                    <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                      <button
                        v-for="v in filteredCondVars"
                        :key="v.key"
                        type="button"
                        class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                        @click="insertVariable(i, v.key)"
                      >
                        {{ resolveLabel(v.label) }}
                      </button>
                      <p v-if="!filteredCondVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">
                        {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>

          </div>
        </template>

        <Button
          ghost
          slate
          sm
          icon="i-lucide-plus"
          :label="t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.ADD_CONDITION')"
          class="self-start"
          @click="addCondition"
        />
      </div>
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Output variable -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{
        t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.SAVE_RESULT_IN')
      }}</span>
      <input
        type="text"
        class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
        :placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.FILTER_ARRAY.OUTPUT_PLACEHOLDER')
        "
        :value="modelValue?.output_variable ?? ''"
        @input="update({ output_variable: $event.target.value })"
      />
    </div>

  </div>
</template>
